import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateMissionSchema, type MissionRecord, type UpdateMission } from '@trajectory/contracts';
import type { z } from 'zod';
import { apiFetch } from '@/lib/api';

/**
 * `z.infer` on a schema with `.default()` fields yields the *output* type,
 * where those fields are required — correct for what the server returns, but
 * unusable for what a caller has to supply. `z.input` is the type before
 * defaults apply, so optional fields stay optional here.
 */
type NewMission = z.input<typeof CreateMissionSchema>;

interface MissionListResponse {
  items: MissionRecord[];
  nextCursor: string | null;
  total: number | null;
}

export function useMissionList(query = '') {
  return useQuery({
    queryKey: ['missions', query],
    queryFn: () =>
      apiFetch<MissionListResponse>(`/missions?limit=100${query ? `&q=${encodeURIComponent(query)}` : ''}`),
  });
}

export function useMission(id: string | null) {
  return useQuery({
    queryKey: ['mission', id],
    queryFn: () => apiFetch<MissionRecord>(`/missions/${id}`),
    enabled: id !== null,
  });
}

export function useCreateMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewMission) => apiFetch<MissionRecord>('/missions', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] }),
  });
}

export function useDeleteMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      apiFetch<void>(`/missions/${id}`, { method: 'DELETE', headers: { 'If-Match': `"${version}"` } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] }),
  });
}

/**
 * Used by the autosave hook: PATCHes with `If-Match` so a stale write
 * surfaces as a 412 rather than silently clobbering. Also writes the saved
 * record straight into the query cache — without this, `useMissionList`'s
 * cached items go stale the moment autosave succeeds, and re-selecting the
 * same mission later (a completely normal thing to do) reloads that stale
 * copy into the store, silently reverting every edit made since the list
 * was last fetched.
 */
export function useSaveMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, patch }: { id: string; version: number; patch: UpdateMission }) =>
      apiFetch<MissionRecord>(`/missions/${id}`, {
        method: 'PATCH',
        headers: { 'If-Match': `"${version}"` },
        body: JSON.stringify(patch),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['mission', saved.id], saved);
      queryClient.setQueriesData<MissionListResponse>({ queryKey: ['missions'] }, (current) =>
        current ? { ...current, items: current.items.map((item) => (item.id === saved.id ? saved : item)) } : current,
      );
    },
  });
}
